<template>
  <v-dialog
    v-model="show"
    width="500"
    class="rounded-xl"
    @click:outside="close"
  >
    <v-card class="pa-5">
      <v-card-actions class="modal-header">
        Result
        <div class="header-actions">
          <!-- Exporta el desglose del detalle: reps (freestyle/live) o ejercicios -->
          <vue-json-to-csv
            v-if="resultData && loading === false && detailCsvData.length > 0"
            class="csv-inline"
            :json-data="detailCsvData"
            :csv-title="detailCsvTitle"
          >
            <v-btn icon color="primary" title="Export detail CSV">
              <v-icon size="20">mdi-download</v-icon>
            </v-btn>
          </vue-json-to-csv>
          <v-btn
            v-if="resultData && loading === false"
            icon
            color="primary"
            title="View JSON"
            @click="showJson = !showJson"
          >
            <v-icon size="20">mdi-code-json</v-icon>
          </v-btn>
          <v-btn icon color="red" @click="close">
            <v-icon size="20">mdi-close</v-icon>
          </v-btn>
        </div>
      </v-card-actions>
      <div v-if="loading" class="loading">
        <v-progress-circular size="64" width="4" color="red" indeterminate />
      </div>

      <!-- JSON completo del resultado, para inspección/copiado -->
      <div v-if="showJson && resultData && loading === false" class="json-viewer">
        <div class="json-toolbar">
          <span class="json-title">Result JSON</span>
          <v-btn x-small color="primary" @click="copyJson">
            <v-icon x-small left>mdi-content-copy</v-icon>
            {{ jsonCopied ? "Copied!" : "Copy JSON" }}
          </v-btn>
        </div>
        <pre class="json-content">{{ resultJson }}</pre>
      </div>

      <result-detail v-if="resultData && loading === false" :item="resultData" />
      <div v-else-if="loading === false">
        Deleted Result
      </div>
    </v-card>
  </v-dialog>
</template>

<script>
import ResultDetail from "./ResultDetail.vue";
export default {
  components: { ResultDetail },
  name: "result-modal",
  model: {},
  props: {
    show: {
      type: Boolean,
      required: true,
      default: false,
    },
    resultId: {
      type: String,
      required: true,
      default: "",
    },
    close: {
      type: Function,
      required: true,
      default: () => {},
    },
    resultUser: undefined,
    resultType: undefined,
  },
  data() {
    return {
      resultData: undefined,
      loading: false,
      showJson: false,
      jsonCopied: false,
    };
  },
  computed: {
    /**
     * Filas para el CSV del detalle según el tipo de resultado:
     * freestyle/live → una fila por rep (historical);
     * coachedClass → una fila por ejercicio (exercisesProgress);
     * preSet/byo → una fila por ejercicio (exercisesSaves).
     */
    detailCsvData() {
      const item = this.resultData;
      if (!item) return [];

      if (
        (item.restype === "freestyle" || item.restype === "live") &&
        Array.isArray(item.historical)
      ) {
        return item.historical.map((rep, index) => ({
          rep: index + 1,
          side: rep?.side || "",
          mxp: rep?.mxp !== undefined ? rep.mxp : Number(rep?.power || 0) / 1000,
          calories: rep?.cals || 0,
          weight_kg: rep?.weight || 0,
          velocity_ms: rep?.velocity || 0,
          distance_m: rep?.distance || 0,
          duration_s: rep?.duration || 0,
          training_zone: rep?.trainingZone ?? "",
          time: rep?.time ?? "",
        }));
      }

      if (item.restype === "coachedClass" && Array.isArray(item.exercisesProgress)) {
        return item.exercisesProgress.map((exercise, index) => {
          const lastKnob =
            exercise?.knobsHistoryExercise?.[
              exercise.knobsHistoryExercise.length - 1
            ];
          return {
            exercise:
              exercise?.exerciseSection?.exercise?.title ||
              `Exercise ${index + 1}`,
            mxp: exercise?.mxp || 0,
            calories: exercise?.calories || 0,
            reps_left: exercise?.reps?.L || 0,
            reps_right: exercise?.reps?.R || 0,
            knob_left: lastKnob?.knobL || 0,
            knob_right: lastKnob?.knobR || 0,
          };
        });
      }

      if (
        (item.restype === "preSet" || item.restype === "byo") &&
        Array.isArray(item.exercisesSaves)
      ) {
        return item.exercisesSaves.map((save, index) => ({
          exercise: save?.exercise?.name?.ENG || `Exercise ${index + 1}`,
          mxp:
            save?.mxp !== undefined
              ? save.mxp
              : Number(save?.powerMadedExercise || 0) / 1000,
          calories: save?.calsBurnedExercise || 0,
          reps_left: save?.repsL || 0,
          reps_right: save?.repsR || 0,
          knob_left: save?.previousL || 0,
          knob_right: save?.previousR || 0,
        }));
      }

      return [];
    },
    detailCsvTitle() {
      const item = this.resultData;
      const type = item?.restype || "result";
      const date =
        item?.createdAt instanceof Date
          ? item.createdAt.toISOString().slice(0, 10)
          : "";
      return `result_detail_${type}_${date}_${this.resultId}`;
    },
    resultJson() {
      if (!this.resultData) return "";
      try {
        // Se omite `user`: lo inyecta el dashboard, no es parte del resultado
        const { user, ...rest } = this.resultData;
        return JSON.stringify(rest, null, 2);
      } catch (error) {
        console.error("Error serializing result JSON:", error);
        return "";
      }
    },
  },
  methods: {
    async getResult(resultId) {
      this.loading = true;
      this.showJson = false;
      this.jsonCopied = false;
      if (this.resultUser && resultId && this.resultType) {
        const resultData = await this.$store.dispatch("getResultData", {
          user: this.resultUser,
          type: this.resultType,
          resultId,
        });
        if (resultData) {
          this.resultData = resultData;
        }
      }
      this.loading = false;
    },
    async copyJson() {
      try {
        if (navigator.clipboard && navigator.clipboard.writeText) {
          await navigator.clipboard.writeText(this.resultJson);
        } else {
          // Fallback para contextos sin Clipboard API
          const textarea = document.createElement("textarea");
          textarea.value = this.resultJson;
          document.body.appendChild(textarea);
          textarea.select();
          document.execCommand("copy");
          document.body.removeChild(textarea);
        }
        this.jsonCopied = true;
        setTimeout(() => {
          this.jsonCopied = false;
        }, 2000);
      } catch (error) {
        console.error("Error copying JSON:", error);
      }
    },
    onClose() {
      this.close();
      this.resultData = undefined;
      this.loading = false;
    },
  },
  watch: {
    resultId: {
      handler: function (newValue, oldValue) {
        this.getResult(newValue);
      },
      deep: true,
      immediate: true,
    },
  },
};
</script>

<style scoped>
.modal-header {
  justify-content: space-between;
  font-size: 18px;
  color: #3a3a3a;
  font-weight: bold;
}
.header-actions {
  display: flex;
  align-items: center;
}
.csv-inline {
  display: inline-block;
}
.loading {
  display: flex;
  justify-content: center;
  align-items: center;
  height: 200px;
}
.json-viewer {
  border: 1px solid #e4e4e4;
  border-radius: 8px;
  margin-bottom: 16px;
  overflow: hidden;
}
.json-toolbar {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 8px 12px;
  background-color: #f5f5f5;
  border-bottom: 1px solid #e4e4e4;
}
.json-title {
  font-size: 13px;
  font-weight: bold;
  color: #3a3a3a;
}
.json-content {
  margin: 0;
  padding: 12px;
  max-height: 320px;
  overflow: auto;
  font-size: 11px;
  line-height: 1.4;
  background-color: #fafafa;
  color: #3a3a3a;
  white-space: pre-wrap;
  word-break: break-word;
  user-select: text;
}
</style>
